import { TextField, type TextFieldProps, Typography } from '../primitives/index.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { focusNextElement } from '../dom/focus.js'
import { VBox } from '../layout/vertical-box.js'
import { useMediaQuery } from '../theme/use-media-query.js'

/** Phones, as the rest of the app reads them */
const phoneQuery = '(max-width:599.95px)'

/** A title is one line: a line break pasted into the phone's wrapping field is saved as a space */
const oneLine = (value: string) => value.replace(/\s*[\r\n]+\s*/g, ' ')

export interface EntityTitleProps extends Omit<TextFieldProps, 'ref'> {
  entity?: Persisted<Entity>
  /** Horizontal alignment of the title and its subtitle. @default 'center' */
  align?: 'center' | 'start'
  /**
   * Make the title the page's h1. The heading takes the input's value as its name, and the textbox
   * inside keeps "Document title". Off by default: Perform already has its own h1 in the header.
   */
  asPageHeading?: boolean
}

export const EntityTitle: FC<EntityTitleProps> = props => {
  const { entity, style, align = 'center', asPageHeading = false, ...inputProps } = props
  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 1000 })
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  /**
   * GH #3110: at 360px a one-line 36px input cut long titles off mid-letter. On phones the title is a one-row
   * textarea that wraps and grows with its text, at the phone h1 size. Desktop keeps the one-line input.
   */
  const isPhone = useMediaQuery(phoneQuery)

  /** Grow the phone field to its lines. Set from scrollHeight: iOS Safari has no CSS `field-sizing`. */
  const fitHeight = () => {
    const field = inputRef.current
    if (!(field instanceof HTMLTextAreaElement)) {
      return
    }
    field.style.height = 'auto'
    field.style.height = `${field.scrollHeight}px`
  }

  const hasEdited = React.useRef(false)

  const handleTitle: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
    if (!entity) {
      return
    }
    hasEdited.current = true
    fitHeight()
    updateEntity(entity.id, { name: oneLine(e.target.value) })
  }
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (inputRef.current) {
        focusNextElement(inputRef.current)
      }
    }
  }

  // Update the input value if the entity name changes externally (e.g., rename dialog)
  useEffect(() => {
    if (!inputRef.current) {
      return
    }
    // Skip if the user is actively editing (input is focused)
    if (document.activeElement === inputRef.current) {
      return
    }
    inputRef.current.value = entity?.name ?? ''
    hasEdited.current = false
    fitHeight()
  }, [entity?.name])

  /** Fit on mount, and again when the width class changes (the field is a new element then) */
  useEffect(() => {
    fitHeight()
  }, [isPhone])

  if (!entity) {
    return null
  }

  const field = (
    <TextField
      key={`${entity.id}-${isPhone ? 'phone' : 'wide'}`}
      variant="standard"
      {...(isPhone ? { multiline: true, rows: 1 } : {})}
      style={{
        fontSize: isPhone ? 'var(--tuna-typography-h1-fontSize)' : 36,
        fontWeight: 300,
        textAlign: align,
        width: '100%',
        margin: 0,
        ...(isPhone
          ? {
              lineHeight: 'var(--tuna-typography-h1-lineHeight)',
              resize: 'none',
              overflow: 'hidden',
              // The primitive's multiline min-height (56px) would pad a one-line title; the height comes from its text
              minHeight: 0,
              // scrollHeight includes the padding, so the height set from it is the border box
              boxSizing: 'border-box',
            }
          : {}),
        ...style,
      }}
      placeholder={'Untitled'}
      aria-label="Document title"
      ref={inputRef as React.Ref<HTMLInputElement>}
      defaultValue={entity.name ?? ''}
      autoFocus={!entity.name}
      autoCorrect="off"
      autoComplete="off"
      onKeyDown={handleKeyDown}
      onChange={handleTitle}
      {...inputProps}
    />
  )

  return (
    <VBox data-entity-title style={{ width: '100%', textAlign: align }}>
      {asPageHeading ? <h1 style={{ margin: 0, font: 'inherit' }}>{field}</h1> : field}
      {entity.description ? <Typography variant="caption">{entity.description}</Typography> : null}
    </VBox>
  )
}

export default EntityTitle
